import React, { useEffect, useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Container, Layout } from '@fiverr-private/layout_components';
import { SECTION_IDS } from '../../../consts';
import { useBI, useImpression, SECTION_NAMES } from '../../bi';
import { DESIGN, TABLET } from './constants';
import { scrollTrack, stickyLayout } from './styles.ve.css';
import { DesktopStage, buildDesktopTimeline } from './DesktopStage';
import { MobileStage } from './MobileStage';

gsap.registerPlugin(ScrollTrigger, useGSAP);

// Scale floor: desktop artboard never shrinks past its 900px tablet rendering.
const SCALE_FLOOR = TABLET.width / DESIGN.width;

export const OurValuesSection = () => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const stickyRef = useRef<HTMLElement>(null);
    const bi = useBI();
    const impressionRef = useImpression<HTMLDivElement>(() =>
        bi.reportSectionImpression({ sectionName: SECTION_NAMES.ourValues })
    );

    const setRootNode = (node: HTMLDivElement | null) => {
        wrapperRef.current = node;
        (impressionRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    };

    // Desktop refs — separate from mobile to avoid collision (2D vs 1D image arrays).
    const desktopCardRefs = useRef<(HTMLElement | null)[]>([]);
    const desktopImageRefs = useRef<(HTMLImageElement | null)[][]>([]);

    // Scale the 1440×988 artboard to fit the viewport, clamped at the tablet floor.
    const [scale, setScale] = useState(1);
    useEffect(() => {
        const compute = () => {
            const host = stickyRef.current;
            if (!host) {
                return;
            }
            const fit = Math.min(host.clientWidth / DESIGN.width, host.clientHeight / DESIGN.height, 1);
            setScale(Math.max(fit, SCALE_FLOOR));
        };
        compute();
        window.addEventListener('resize', compute);
        return () => window.removeEventListener('resize', compute);
    }, []);

    useGSAP(
        () => {
            const mm = gsap.matchMedia();

            mm.add(`(min-width: ${TABLET.width}px)`, () => {
                buildDesktopTimeline({
                    wrapperRef,
                    cardRefs: desktopCardRefs,
                    imageRefs: desktopImageRefs,
                });
            });

            return () => mm.revert();
        },
        { scope: wrapperRef }
    );

    return (
        <Container ref={setRootNode} id={SECTION_IDS.values} backgroundColor="white" className={scrollTrack}>
            <Layout ref={stickyRef} className={stickyLayout}>
                <DesktopStage
                    hidden={{ default: true, md: false }}
                    scale={scale}
                    cardRefs={desktopCardRefs}
                    imageRefs={desktopImageRefs}
                />
                <MobileStage hidden={{ default: false, md: true }} />
            </Layout>
        </Container>
    );
};
